import { clientState } from '../services/clientState';
// audit-safety-integrated
import { readJson } from '../utils/auditSafety';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';

// Flatten the nested /user response into a list of unique branches.
// The response can be shaped as { data: [{ department, branch_type, branches: [{ name, users: [...] }] }] }
// or as a users list. We handle both by walking the tree.
const flattenBranches = (data) => {
  const branches = [];
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) { node.forEach(walk); return; }
    // If this looks like a branch (has name + users or branch_type)
    if (node.branch_type && (node.name || node.branch)) {
      branches.push({
        key: `${node.branch_type}__${node.branch || node.name}`,
        branchName: node.name || node.branch,
        branchCode: node.branch || node.name,
        branchType: node.branch_type,
        department: node.department || node.depatment || '',
        district: node.district || node.users?.[0]?.district || '',
        location: node.location || node.users?.[0]?.location || '',
        contact: node.contact || node.users?.[0]?.contact || '',
        address: node.address || node.users?.[0]?.address || '',
        eaters: node.eaters ?? node.users?.[0]?.eaters ?? '',
        users: node.users || [],
      });
    }
    Object.values(node).forEach((v) => {
      if (v && typeof v === 'object') walk(v);
    });
  };
  walk(data);
  // dedupe
  const seen = new Set();
  return branches.filter(b => {
    if (seen.has(b.key)) return false;
    seen.add(b.key);
    return true;
  });
};

const EaterCount = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [trackEaters, setTrackEaters] = useState(false);
  const [eaters, setEaters] = useState('');
  const [search, setSearch] = useState('');

  const role = (() => {
    try {
      const u = JSON.parse(clientState.getItem('user') || '{}');
      return (u?.role || '').toLowerCase().trim();
    } catch {
      return '';
    }
  })();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await fetch(`${API_BASE_URL}/user`, {
          headers: {

            'Content-Type': 'application/json',
          },
        });
        const data = await readJson(res);
        if (cancelled) return;
        if (data?.error) {
          setError(data?.message || 'Failed to load users');
          return;
        }
        const list = flattenBranches(data?.data || data);
        setBranches(list);
      } catch (e) {
        if (!cancelled) setError('Failed to load users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = useMemo(
    () => branches.find(b => b.key === selectedKey) || null,
    [branches, selectedKey]
  );

  // When a branch is picked, prefill trackEaters/eaters from it
  useEffect(() => {
    if (!selected) {
      setTrackEaters(false);
      setEaters('');
      return;
    }
    const existing = selected.eaters;
    if (existing && Number(existing) > 0) {
      setTrackEaters(true);
      setEaters(String(existing));
    } else {
      setTrackEaters(false);
      setEaters('');
    }
    setSuccess('');
    setError('');
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredBranches = useMemo(() => {
    if (!search.trim()) return branches;
    const q = search.toLowerCase();
    return branches.filter(b =>
      (b.branchName || '').toLowerCase().includes(q) ||
      (b.branchType || '').toLowerCase().includes(q) ||
      (b.district || '').toLowerCase().includes(q) ||
      (b.users || []).some(u => (u.username || u.name || '').toLowerCase().includes(q))
    );
  }, [branches, search]);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!selected) {
      setError('Please select a user/unit.');
      return;
    }
    if (trackEaters && (eaters === '' || Number(eaters) <= 0)) {
      setError('Please enter a valid eater count.');
      return;
    }
    setSaving(true);
    try {

      const url = `${API_BASE_URL}/user/branch/${encodeURIComponent(selected.branchType)}/${encodeURIComponent(selected.branchCode)}`;
      const payload = {
        eaters: trackEaters ? String(eaters) : '',
      };
      const res = await fetch(url, {
        method: 'PUT',
        headers: {

          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await readJson(res);
      if (res.ok && !data?.error) {
        setSuccess('Eater count saved successfully.');
        // Update local branches so the list reflects the new value
        setBranches(prev => prev.map(b => b.key === selected.key
          ? { ...b, eaters: trackEaters ? String(eaters) : '' }
          : b));
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data?.message || 'Save failed.');
      }
    } catch (e) {
      setError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (role !== 'st-ind-apr') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <h1 className="text-xl font-bold text-white">Eater Count</h1>
            <p className="text-xs text-blue-100 mt-0.5">Select a user / unit and set its eater count.</p>
          </div>

          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <svg className="w-6 h-6 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          ) : (
            <div className="p-6 grid md:grid-cols-5 gap-6">
              {/* Left: user/unit list */}
              <div className="md:col-span-2 border border-gray-200 rounded-lg overflow-hidden flex flex-col">
                <div className="p-2 border-b bg-gray-50">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search unit / user..."
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {filteredBranches.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-400 italic">No units found.</div>
                  ) : filteredBranches.map(b => (
                    <button
                      key={b.key}
                      onClick={() => setSelectedKey(b.key)}
                      className={`w-full text-left px-3 py-2 border-b border-gray-100 transition-colors ${
                        selectedKey === b.key ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-gray-800 truncate">{b.branchName}</div>
                        {b.branchType && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold whitespace-nowrap">
                            {b.branchType}
                          </span>
                        )}
                      </div>
                      {b.district && (
                        <div className="text-xs text-gray-500 truncate mt-0.5">{b.district}</div>
                      )}
                      {b.eaters && Number(b.eaters) > 0 && (
                        <div className="text-[11px] text-emerald-700 mt-0.5">Eaters: {b.eaters}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: form */}
              <div className="md:col-span-3 space-y-4">
                {!selected ? (
                  <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-sm text-gray-500">
                    Select a unit from the list to set the eater count.
                  </div>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-900 space-y-1">
                      <div><span className="font-semibold">Unit:</span> {selected.branchName}</div>
                      <div><span className="font-semibold">Type:</span> {selected.branchType}</div>
                      {selected.district && (
                        <div><span className="font-semibold">District:</span> {selected.district}</div>
                      )}
                      {selected.users?.length > 0 && (
                        <div><span className="font-semibold">Users:</span> {selected.users.map(u => u.username || u.name).filter(Boolean).join(', ')}</div>
                      )}
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={trackEaters}
                          onChange={(e) => {
                            setTrackEaters(e.target.checked);
                            if (!e.target.checked) setEaters('');
                          }}
                          className="mt-1 w-4 h-4"
                        />
                        <div>
                          <div className="text-sm font-semibold text-gray-800">Limit monthly eater count</div>
                          <div className="text-xs text-gray-500">Tick to enter the number of eaters for this unit.</div>
                        </div>
                      </label>

                      {trackEaters && (
                        <div className="pl-7">
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Eater Count</label>
                          <input
                            type="number"
                            min="0"
                            value={eaters}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEaters(v === '' ? '' : String(Math.max(0, Number(v))));
                            }}
                            placeholder="Enter count"
                            className="w-40 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>

                    {error && (
                      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                        {error}
                      </div>
                    )}
                    {success && (
                      <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                        {success}
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`px-5 py-2 text-sm font-semibold rounded-lg shadow-md ${
                          saving
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800'
                        }`}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EaterCount;
